<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import DAlert from '../../design/components/DAlert.vue';
import DButton from '../../design/components/DButton.vue';
import DDialog from '../../design/components/DDialog.vue';
import DField from '../../design/components/DField.vue';
import DInput from '../../design/components/DInput.vue';
import DTextarea from '../../design/components/DTextarea.vue';
import ColorPicker from '../../design/components/ColorPicker.vue';
import type { ProjectColor } from '../../app/api/ProjectsApi.ts';
import { useFormSubmit } from '../../app/composables/useFormSubmit.ts';
import { suggestKey } from '../../app/projectKey.ts';
import { useWorkspaceStore } from '../../app/stores/workspace.ts';

const open = defineModel<boolean>('open', { required: true });
const props = defineProps<{ org: string }>();

const workspace = useWorkspaceStore();
const router = useRouter();
const { pending, formError, fieldErrors, submit, reset } = useFormSubmit();

const name = ref('');
const key = ref('');
const keyEdited = ref(false);
const description = ref('');
const color = ref<ProjectColor>('blue');

watch(name, (value) => {
  if (!keyEdited.value) key.value = suggestKey(value);
});
watch(open, (isOpen) => {
  if (!isOpen) return;
  name.value = key.value = description.value = '';
  keyEdited.value = false;
  color.value = 'blue';
  reset();
});

const keyHint = computed(() => `Item keys will look like ${key.value || 'KEY'}-1.`);

function onKeyInput(value: string) {
  keyEdited.value = true;
  key.value = value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 10);
}

async function onSubmit() {
  let created: { key: string } | null = null;
  const ok = await submit(async () => {
    created = await workspace.createProject(props.org, {
      name: name.value,
      key: key.value,
      description: description.value,
      color: color.value,
    });
  });
  if (ok && created) {
    open.value = false;
    await router.push({
      name: 'project',
      params: { org: props.org, key: (created as { key: string }).key },
    });
  }
}
</script>

<template>
  <DDialog
    v-model:open="open"
    title="New project"
    description="Projects hold your work items, sprints, whiteboards and docs."
  >
    <DAlert v-if="formError" tone="error">{{ formError }}</DAlert>
    <form class="form" novalidate @submit.prevent="onSubmit">
      <DField v-slot="field" label="Name" :error="fieldErrors['name']">
        <DInput v-bind="field" v-model="name" maxlength="80" autocomplete="off" required />
      </DField>
      <DField v-slot="field" label="Key" :error="fieldErrors['key']" :hint="keyHint">
        <DInput
          v-bind="field"
          :model-value="key"
          class="input--mono"
          maxlength="10"
          autocomplete="off"
          autocapitalize="characters"
          spellcheck="false"
          required
          @update:model-value="onKeyInput"
        />
      </DField>
      <DField v-slot="field" label="Description" :error="fieldErrors['description']">
        <DTextarea v-bind="field" v-model="description" maxlength="2000" placeholder="Optional" />
      </DField>
      <ColorPicker v-model="color" label="Color" />
      <div class="dialog__actions">
        <DButton @click="open = false">Cancel</DButton>
        <DButton type="submit" variant="primary" :loading="pending">{{
          pending ? 'Creating…' : 'Create project'
        }}</DButton>
      </div>
    </form>
  </DDialog>
</template>
